/**
 * Lightweight Google Analytics (gtag.js) helper.
 *
 * The Google tag is loaded from index.html. Every call here is a safe no-op
 * when the tag is blocked (ad blockers, preview environments, SSR), so page
 * behaviour never depends on analytics being available.
 */

type GtagParams = Record<string, unknown>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Fire a custom GA4 event. Silently ignored if gtag is unavailable. */
export function trackEvent(eventName: string, params: GtagParams = {}): void {
  if (typeof window === "undefined") return;
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  gtag("event", eventName, params);
}

/**
 * Lead conversion — fired when a quote / contact / callback form submits
 * successfully. Uses the GA4 recommended "generate_lead" event name.
 *
 * The current page path is attached automatically so conversions can be broken
 * down per page in GA4 (Reports → Engagement → Conversions by page) without
 * every caller having to pass it.
 */
export function trackLead(formId: string, extra: GtagParams = {}): void {
  const pagePath = typeof window !== "undefined" ? window.location.pathname : "unknown";
  trackEvent("generate_lead", { form_id: formId, page_path: pagePath, ...extra });
}

/**
 * Contact intent — fired when a visitor taps a call, WhatsApp or email link.
 * channel: "phone" | "whatsapp" | "email"
 */
export function trackContactClick(
  channel: "phone" | "whatsapp" | "email",
  location: string,
): void {
  trackEvent("contact_click", {
    contact_channel: channel,
    link_location: location || "unknown",
  });
}

/**
 * CTA button click — fired for prominent call-to-action buttons (quote, call,
 * etc.). `label` is the button text, `location` is where on the page it sits.
 */
export function trackButtonClick(label: string, location: string): void {
  trackEvent("cta_click", {
    button_label: label || "unknown",
    button_location: location || "unknown",
  });
}

/**
 * Form start — fired the first time a visitor interacts with a lead form, so
 * drop-off between starting and submitting can be measured.
 */
export function trackFormStart(formId: string): void {
  trackEvent("form_start", { form_id: formId });
}

/**
 * Scroll depth — fired as the visitor passes each threshold (25/50/75/90%) on a
 * page, so you can see how far people read before they convert.
 */
export function trackScrollDepth(pagePath: string, percent: number): void {
  trackEvent("scroll_depth", {
    page_path: pagePath || "unknown",
    percent_scrolled: percent,
  });
}